import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import { useReopenModalOnMount } from '@/lib/stockModalRestore';
import { toast } from 'sonner';
import {
  ArrowLeft, BookOpen, Plus, Search, Calendar, Tag, Edit3,
  Trash2, ClipboardCheck, AlertCircle, Hash, Heart, Brain,
  TrendingDown, AlertTriangle, Lock, Twitter,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import JournalEntryModal from '@/components/JournalEntryModal';
import ReviewModal from '@/components/ReviewModal';
import ConvictionOutcomeChart from '@/components/ConvictionOutcomeChart';
import LoginModal from '@/components/LoginModal';
import InlineStockModal from '@/components/InlineStockModal';
import { shareEntryToX } from '@/utils/journalShare';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Journal — a dividend-investor's reflective log. Entries are written
// at decision moments (buy / sell / dividend cut / ex-div week / weekly
// review) so the user has a written record of their thesis BEFORE
// outcomes are known. Inspired by TraderSync / Edgewonk / Tradervue
// but tilted toward the dividend-niche needs: thesis on add, thesis on
// trim, dividend-cut response, ex-div week observations.

const ENTRY_TYPE_META = {
  thesis_add:   { label: 'Thesis on Add',     hue: 'emerald', Icon: Plus,           hint: 'Why am I buying this?' },
  thesis_sell:  { label: 'Thesis on Sell',    hue: 'rose',    Icon: TrendingDown,   hint: 'Why am I selling? Regret check.' },
  dividend_cut: { label: 'Dividend-Cut Note', hue: 'amber',   Icon: AlertCircle,    hint: 'Hold or sell? Document your reasoning.' },
  ex_div:       { label: 'Ex-Div Week',       hue: 'cyan',    Icon: Calendar,       hint: 'Notes during ex-div week.' },
  free:         { label: 'Free Note',         hue: 'slate',   Icon: BookOpen,       hint: 'Anything on your mind.' },
  review:       { label: 'Periodic Review',   hue: 'violet',  Icon: ClipboardCheck, hint: 'Weekly or monthly self-review.' },
  jnl_starter:  { label: 'JNL Starter',       hue: 'amber',   Icon: AlertTriangle,  hint: 'Drafted from a Robinhood JNL row — fill in your context.' },
};

const EMOTION_META = {
  disciplined: { label: 'Disciplined', emoji: '🧘' },
  fomo:        { label: 'FOMO',        emoji: '😬' },
  panic:       { label: 'Panic',       emoji: '😨' },
  patient:     { label: 'Patient',     emoji: '🪷' },
  curious:     { label: 'Curious',     emoji: '🤔' },
  regret:      { label: 'Regret',      emoji: '😞' },
  confident:   { label: 'Confident',   emoji: '😎' },
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
           ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function HueBadge({ hue, children, ...rest }) {
  const cls = {
    emerald: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300',
    rose:    'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300',
    amber:   'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300',
    cyan:    'bg-cyan-100 text-cyan-800 dark:bg-cyan-950/60 dark:text-cyan-300',
    slate:   'bg-slate-100 text-slate-800 dark:bg-slate-800/60 dark:text-slate-300',
    violet:  'bg-violet-100 text-violet-800 dark:bg-violet-950/60 dark:text-violet-300',
  }[hue] || 'bg-muted text-muted-foreground';
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold ${cls}`} {...rest}>{children}</span>;
}

export default function Journal() {
  const { user } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [reviewStatus, setReviewStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // filters
  const [tickerFilter, setTickerFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [search, setSearch] = useState('');

  // modals
  const [editing, setEditing] = useState(null); // entry or null
  const [showNew, setShowNew] = useState(false);
  const [reviewKind, setReviewKind] = useState(null); // 'weekly' | 'monthly' | null
  // Round 71p (Feb 2026): in-place stock modal so clicking a ticker
  // chip opens it as an overlay on the Journal page instead of
  // navigating to the Dashboard. When the user closes it, they stay
  // on /journal. If they bought/sold inside the modal we re-fetch
  // entries (so any context-snapshot stats stay fresh).
  const [openStockTicker, setOpenStockTicker] = useState(null);

  // Round 79c — StockModal back-restore. If user pressed Back from a
  // page opened via the modal's "View all" link, re-open the modal at
  // that ticker on mount.
  useReopenModalOnMount(setOpenStockTicker);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const params = {};
      if (tickerFilter) params.ticker = tickerFilter;
      if (typeFilter) params.entry_type = typeFilter;
      if (tagFilter) params.tag = tagFilter;
      if (search) params.q = search;
      const [entriesRes, statsRes, reviewRes] = await Promise.all([
        axios.get(`${API}/journal/entries`, { params, withCredentials: true }),
        axios.get(`${API}/journal/stats`, { withCredentials: true }),
        axios.get(`${API}/journal/review-status`, { withCredentials: true }),
      ]);
      setEntries(entriesRes.data.entries || []);
      setStats(statsRes.data);
      setReviewStatus(reviewRes.data);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  // Re-fetch on filter change with light debounce on search.
  useEffect(() => {
    const t = setTimeout(fetchAll, search ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [tickerFilter, typeFilter, tagFilter, search]);

  const allTickers = useMemo(() =>
    Object.keys(stats?.by_ticker || {}).sort(),
    [stats]);

  const handleDelete = async (entryId) => {
    if (!window.confirm('Delete this journal entry permanently?')) return;
    try {
      await axios.delete(`${API}/journal/entries/${entryId}`, { withCredentials: true });
      toast.success('Entry deleted');
      fetchAll();
    } catch (err) {
      toast.error('Delete failed', { description: err?.response?.data?.detail || err.message });
    }
  };

  // Round 71n (Feb 2026): Journal is logged-in only — when not signed
  // in we render a gate explaining what the journal is and why
  // logging in protects their thinking. The gate replaces the rest
  // of the page so we never call the journal endpoints when there's
  // no user (those would 401 anyway).
  if (user === null || user === false) {
    return (
      <div className="min-h-screen min-h-[100svh] bg-background">
        <div className="max-w-2xl mx-auto px-3 sm:px-4 pt-8 pb-0 space-y-5">
          <BackOrHomeButton testid="journal-back-btn" />

          <div className="rounded-2xl border-2 border-violet-300 dark:border-violet-700 bg-muted/20 p-6 sm:p-8 text-center" data-testid="journal-login-gate">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-violet-100 dark:bg-violet-950/60 mb-3">
              <BookOpen className="w-6 h-6 text-violet-600 dark:text-violet-400" />
            </div>
            <h1 className="text-2xl font-bold">Investing Journal</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              Log in to view or start journaling your dividend stock portfolio.
              Your reflections are private — only you can read them.
            </p>

            <div className="text-left max-w-md mx-auto mt-5 space-y-1.5 text-sm">
              {[
                'Capture your thesis on every buy and sell',
                'Auto-prompt when one of your holdings cuts its dividend',
                'Weekly & monthly self-review templates',
                'Conviction vs Outcome chart (unlocks at 20 entries)',
                'Per-stock tab inside every Stock Modal',
                'One-click share-to-X for any single entry',
              ].map((line, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-violet-600 dark:text-violet-400 shrink-0 mt-0.5">✓</span>
                  <span>{line}</span>
                </div>
              ))}
            </div>

            <Button
              onClick={() => setShowLogin(true)}
              className="mt-5 bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="journal-login-cta"
            >
              <Lock className="w-4 h-4 mr-1" /> Sign Up / Log In — It's Free
            </Button>
            <p className="text-[10px] text-muted-foreground/70 mt-3">
              Journal entries are <span className="font-semibold">private by default</span>. You can share any single entry to X manually if you want.
            </p>
          </div>
        </div>
        <SiteDisclaimer />

        {showLogin && <LoginModal open={showLogin} onClose={() => setShowLogin(false)} />}
      </div>
    );
  }

  return (
    <div className="min-h-screen min-h-[100svh] bg-background">
      <div className="max-w-5xl mx-auto px-3 sm:px-4 pt-5 pb-0 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <BackOrHomeButton testid="journal-back-btn" />
          <Link
            to="/glossary#entry-journal"
            target="_blank"
            rel="noopener"
            title="Open the Journal definition in the Glossary"
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            What is a journal?
          </Link>
        </div>

        <div className="flex items-center gap-3">
          <BookOpen className="w-7 h-7 text-violet-600 dark:text-violet-400" />
          <div>
            <h1 className="text-3xl font-bold">Investing Journal</h1>
            <p className="text-sm text-muted-foreground">
              {stats?.total ? `${stats.total} ${stats.total === 1 ? 'entry' : 'entries'}` : 'No entries yet'}
              {stats?.latest_entry_at && <> · last entry {formatDate(stats.latest_entry_at)}</>}
            </p>
          </div>
        </div>

        {/* Review status + New Entry */}
        <div className="flex flex-wrap gap-2 items-center">
          <Button
            onClick={() => setShowNew(true)}
            data-testid="journal-new-entry-btn"
            className="bg-violet-600 hover:bg-violet-700 text-white"
          >
            <Plus className="w-4 h-4 mr-1" /> New entry
          </Button>
          {/* Round 71o (Feb 2026): review buttons only render when
              the backend says they're due. Backend gates on first-
              entry-was-7d-ago + no-recent-review + not-snoozed, so
              users who never journal never see this prompt and users
              who just started get a fresh week to write before the
              prompt fires. */}
          {reviewStatus?.weekly?.due && (
            <Button
              variant="outline"
              onClick={() => setReviewKind('weekly')}
              data-testid="journal-weekly-review-btn"
              className="border-amber-500 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/20"
            >
              <ClipboardCheck className="w-4 h-4 mr-1" />
              Weekly review
              <span className="ml-1 text-[10px] font-bold">·DUE</span>
            </Button>
          )}
          {reviewStatus?.monthly?.due && (
            <Button
              variant="outline"
              onClick={() => setReviewKind('monthly')}
              data-testid="journal-monthly-review-btn"
              className="border-amber-500 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/20"
            >
              <ClipboardCheck className="w-4 h-4 mr-1" />
              Monthly review
              <span className="ml-1 text-[10px] font-bold">·DUE</span>
            </Button>
          )}
        </div>

        {/* Conviction-vs-Outcome chart — gates itself; renders a
            "keep going" placeholder until 20+ qualifying entries. */}
        <ConvictionOutcomeChart />

        {/* Filters */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search title or body…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="journal-search-input"
              className="pl-8"
            />
          </div>
          <select
            value={tickerFilter}
            onChange={(e) => setTickerFilter(e.target.value)}
            data-testid="journal-ticker-filter"
            className="h-10 px-2 rounded-md border bg-background text-sm"
          >
            <option value="">All tickers</option>
            {allTickers.map(t => <option key={t} value={t}>{t} ({stats.by_ticker[t]})</option>)}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            data-testid="journal-type-filter"
            className="h-10 px-2 rounded-md border bg-background text-sm"
          >
            <option value="">All types</option>
            {Object.entries(ENTRY_TYPE_META).map(([k, m]) => (
              <option key={k} value={k}>{m.label}{stats?.by_type?.[k] ? ` (${stats.by_type[k]})` : ''}</option>
            ))}
          </select>
          <Input
            placeholder="Filter by tag (no #)"
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            data-testid="journal-tag-filter"
          />
        </div>

        {/* Entries timeline */}
        {loading && <div className="text-center py-10 text-muted-foreground" data-testid="journal-loading">Loading…</div>}
        {error && <div className="text-center py-10 text-rose-600" data-testid="journal-error">{error}</div>}
        {!loading && !error && entries.length === 0 && (
          <div className="text-center py-12 border-2 border-dashed border-border rounded-xl" data-testid="journal-empty">
            <BookOpen className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
            <p className="text-foreground font-semibold">Your journal is empty.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Capture your thinking next time you buy, sell, or read about a holding. <br className="hidden sm:block" />
              Future-you will thank present-you.
            </p>
            <Button
              onClick={() => setShowNew(true)}
              className="mt-4 bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="journal-empty-cta"
            >
              <Plus className="w-4 h-4 mr-1" /> Write your first entry
            </Button>
          </div>
        )}

        {entries.length > 0 && <div className="space-y-3">
          {entries.map((e) => {
            const meta = ENTRY_TYPE_META[e.entry_type] || ENTRY_TYPE_META.free;
            const Icon = meta.Icon;
            const emo = e.emotion ? EMOTION_META[e.emotion] : null;
            return (
              <div
                key={e.entry_id}
                className="rounded-xl border-2 border-border bg-muted/20 p-4 hover:border-violet-400/60 transition-colors"
                data-testid={`journal-entry-${e.entry_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <HueBadge hue={meta.hue}><Icon className="w-3 h-3" />{meta.label}</HueBadge>
                      {e.ticker && (
                        <button
                          type="button"
                          onClick={() => setOpenStockTicker(e.ticker)}
                          className="px-2 py-0.5 rounded-md text-[11px] font-mono font-bold bg-background border hover:bg-accent hover:border-violet-400 transition-colors"
                          data-testid={`journal-entry-ticker-${e.entry_id}`}
                          title={`Open ${e.ticker} stock modal`}
                        >
                          {e.ticker}
                        </button>
                      )}
                      {emo && (
                        <span className="text-[11px] text-muted-foreground" title={emo.label}>
                          {emo.emoji} {emo.label}
                        </span>
                      )}
                      {e.conviction != null && (
                        <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                          <Brain className="w-3 h-3" /> conviction {e.conviction}/10
                        </span>
                      )}
                    </div>
                    {e.title && <h3 className="text-base font-bold">{e.title}</h3>}
                    <p className="text-sm text-foreground/90 whitespace-pre-wrap mt-1">{e.body}</p>
                    {e.tags && e.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {e.tags.map(t => (
                          <Link
                            key={t}
                            to={`/journal/tag/${t}`}
                            className="text-[10px] text-muted-foreground hover:text-violet-600 dark:hover:text-violet-400"
                            data-testid={`journal-tag-${t}`}
                            title={`Show all entries on #${t}`}
                          >
                            #{t}
                          </Link>
                        ))}
                      </div>
                    )}
                    <div className="text-[10px] text-muted-foreground mt-2">
                      {formatDate(e.created_at)}
                      {e.updated_at && e.updated_at !== e.created_at && <span> · edited {formatDate(e.updated_at)}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button
                      onClick={() => shareEntryToX(e)}
                      className="p-1.5 rounded hover:bg-sky-100 dark:hover:bg-sky-950/40 text-sky-600"
                      title="Share this entry to X"
                      data-testid={`journal-share-x-${e.entry_id}`}
                    >
                      <Twitter className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setEditing(e)}
                      className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                      title="Edit"
                      data-testid={`journal-edit-${e.entry_id}`}
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(e.entry_id)}
                      className="p-1.5 rounded hover:bg-rose-100 dark:hover:bg-rose-950/40 text-rose-600"
                      title="Delete"
                      data-testid={`journal-delete-${e.entry_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>}
      </div>
      <SiteDisclaimer />

      {/* New entry modal */}
      {showNew && (
        <JournalEntryModal
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); fetchAll(); }}
        />
      )}
      {editing && (
        <JournalEntryModal
          entry={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchAll(); }}
        />
      )}
      {reviewKind && (
        <ReviewModal
          kind={reviewKind}
          onClose={() => setReviewKind(null)}
          onSaved={() => { setReviewKind(null); fetchAll(); }}
        />
      )}

      {/* Round 71p: in-place Stock Modal — clicking a ticker chip on
          this page opens it as an overlay; closing returns the user
          here (NOT to the Dashboard). Re-fetches journal entries on
          close only if a buy/sell happened inside the modal. */}
      <InlineStockModal
        ticker={openStockTicker}
        onClose={() => setOpenStockTicker(null)}
        onPortfolioChanged={() => fetchAll()}
      />
    </div>
  );
}
